import styled from '@emotion/styled';
import { useRequest } from 'ahooks';
import { Button, Form, Input, Radio, Spin, message } from 'antd';
import { useTFERPEnable } from 'hooks/useTFERPEnable';
import { useRefreshTFERPVersionValue } from 'hooks/useTFERPVersion';
import { useState } from 'react';
import { useTranslate } from '@tolgee/react';
import {
  getTFERPParams,
  getTFERPSecret,
  saveTFERPParams,
  saveTFERPSecret,
} from 'services/api/tferp';
import { isURL } from 'utils/is';

interface FormValues {
  secret: string;
  versionType: 'saas' | 'self';
  baseUrl?: string;
}

const TFERP = () => {
  const { t } = useTranslate();
  const [form] = Form.useForm<FormValues>();

  const [showHostField, setShowHostField] = useState(false);

  const tferpEnable = useTFERPEnable();
  const refreshTFERPVersion = useRefreshTFERPVersionValue(tferpEnable);

  const { loading } = useRequest(
    async () => {
      const [paramsRes, secretRes] = await Promise.all([getTFERPParams(), getTFERPSecret()]);
      return {
        secret: secretRes.data.secret,
        versionType: (paramsRes.data.kvs.find(({ key }) => key === 'versionType')?.value ??
          'saas') as FormValues['versionType'],
        baseUrl: paramsRes.data.kvs.find(({ key }) => key === 'baseUrl')?.value,
      };
    },
    {
      onSuccess(data) {
        form.setFieldsValue(data);
        setShowHostField(data.versionType === 'self');
      },
    },
  );

  const { loading: saveLoading, runAsync: saveForm } = useRequest(
    (params: any, secret: string) => {
      const keys = Object.keys(params);
      const response = Promise.all([
        saveTFERPParams(keys.map((key) => ({ key, value: params[key] }))),
        saveTFERPSecret(secret),
      ]);
      return response;
    },
    {
      manual: true,
      onSuccess() {
        message.success(t('operation.saveSucceed'));
        refreshTFERPVersion();
      },
      onError() {
        message.error(t('operation.saveFailed'));
      },
    },
  );

  return (
    <div className="p-4 h-full flex flex-col">
      <div className="flex-1 bg-fill-0">
        <header className="px-5 h-[60px] flex items-center border-0 border-b border-solid border-fill-300">
          <h2 className="mb-0">{t('tab.tferp')}</h2>
        </header>
        <Spin spinning={loading}>
          <div className="p-6">
            <Title className="relative pl-3 mb-4 font-semibold">
              {t('tferp_title_sider')}
              <a
                className="ml-2 font-normal text-xs"
                href="https://www.yuque.com/wangxiaowangerp/tqd0g4/qml9id8ecynqfifp"
                target="_blank"
                rel="noreferrer"
              >
                {t('tferp_doc_link')}
              </a>
            </Title>

            <Form
              className="w-[480px]"
              layout="vertical"
              form={form}
              initialValues={{
                versionType: 'saas',
                secret: '',
              }}
              onFinish={(values) => {
                const { secret, ...params } = values;
                saveForm(params, secret);
              }}
              onValuesChange={(_, values) => {
                setShowHostField(values.versionType === 'self');
              }}
            >
              <Form.Item
                label={t('tferp_field_label_secret')}
                name="secret"
                required
                rules={[{ required: true, message: t('tferp_field_placeholder_secret') }]}
              >
                <Input placeholder={t('tferp_field_placeholder_secret')} />
              </Form.Item>

              <Form.Item name="versionType">
                <Radio.Group>
                  <Radio value="saas">{t('tferp_field_version_type_saas')}</Radio>
                  <Radio value="self">{t('tferp_field_version_type_self')}</Radio>
                </Radio.Group>
              </Form.Item>

              {showHostField && (
                <Form.Item
                  label={t('tferp_field_label_baseurl')}
                  name="baseUrl"
                  required
                  rules={[
                    { required: true, message: t('tferp_field_placeholder_baseurl') },
                    {
                      validator: (_, values: string) => {
                        if (!isURL(values)) {
                          return Promise.reject(t('tferp_field_valid_url'));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input placeholder="https://" />
                </Form.Item>
              )}

              <Form.Item>
                <Button loading={saveLoading} type="primary" htmlType="submit">
                  {t('marketing.保存')}
                </Button>
              </Form.Item>
            </Form>
          </div>
        </Spin>
      </div>
    </div>
  );
};

export default TFERP;

const Title = styled.div`
  &:before {
    content: '';
    position: absolute;
    border-left: 4px solid var(--fett-colors-primary);
    height: 1em;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
  }
`;
